import React from 'react';
import { Canvas } from '@react-three/fiber';

import { LashingDetails, DirectLashingInfo } from '../../types/calculation';
import CameraController from '../shared3d/CameraController';
import { AxisLabel } from '../shared3d/LineRenderers';
import CargoBox from '../shared3d/CargoBox';
import { DirectLashing, LoopLashing, SupportBlock } from './LashingElements';
import DRing from '../shared3d/DRing';

import {
    AXIS_LENGTH_MULTIPLIER,
    LASHING_ATTACH_INSET,
    DEFAULT_CAMERA_POSITION,
    DEFAULT_CAMERA_FOV,
    AMBIENT_LIGHT_INTENSITY_DARK,
    AMBIENT_LIGHT_INTENSITY_LIGHT,
    DIRECTIONAL_LIGHT_INTENSITY_DARK,
    DIRECTIONAL_LIGHT_INTENSITY_LIGHT
} from '../../constants/visualization';

// Type definitions for component props
interface LashingSceneProps {
    dimensions: {
        width: number;
        height: number;
        depth: number;
        scale?: number;
    };
    cog?: {
        width: number;
        length: number;
        height: number;
    };
    theme?: 'light' | 'dark';
    lashingDetails?: LashingDetails;
    zoom?: number;
    onZoomChange?: (zoom: number) => void;
    viewPreset?: 'top' | 'front' | 'side' | 'perspective';
    cameraAngle?: { azimuth: number; elevation: number } | null;
    transportUnit?: string;
    breakBulkOrientation?: 'longitudinal' | 'transverse';
    targetYOffset?: number; // Add targetYOffset to props
    isFullscreen?: boolean;
}

const LashingPrism: React.FC<LashingSceneProps> = React.memo(({ dimensions, cog, theme = 'dark', lashingDetails, transportUnit, breakBulkOrientation, isFullscreen }) => {
    const { width, height, depth } = dimensions;

    const halfL = width / 2;
    const halfH = height / 2;
    const halfW = depth / 2;

    const axisLength = Math.max(width, height, depth) * AXIS_LENGTH_MULTIPLIER;
    const isDark = theme === 'dark';
    const axisColorZ = isDark ? '#4d4dff' : '#0000cc'; // Blue for Right/Left;
    const scaleFactor = dimensions.scale || 1;

    // User Request: Standardize 3D graphics proportions across all transport units 
    // based on the 20' Flatrack baseline (5.62m x 2.24m). 
    // Transport type has no effect on the visual world boundary or framing.
    const unitHalfL = (5.62 * scaleFactor) / 2;
    const unitHalfW = (2.24 * scaleFactor) / 2;

    const labelOffsetL = halfL + Math.max(1.2, width * 0.15);
    const labelOffsetW = halfW + Math.max(1.5, depth * 0.15);

    // Responsive Label Scale: Ensure labels don't disappear on large cargo
    // Reduction in fullscreen to compensate for the 70% zoom-out of the cargo
    const maxDim = Math.max(width, height, depth);
    const baseLabelScale = Math.max(0.7, Math.min(2.0, maxDim / 10));
    const responsiveLabelScale = isFullscreen ? baseLabelScale * 0.8 : baseLabelScale;

    const lashingSegments: {
        start: [number, number, number];
        end: [number, number, number];
        midPoint?: [number, number, number];
        color: string;
        startRotation: [number, number, number];
    }[] = [];

    // New approach: All floor anchors on two parallel lines
    if (lashingDetails) {
        const allLashings: Array<{
            start: [number, number, number];
            color: string;
            startRotation: [number, number, number];
            direction: 'forward' | 'backward' | 'right' | 'left';
            angle?: number;
        }> = [];

        // Helper to add lashings from each direction
        const collectLashings = (lashings: DirectLashingInfo[], dir: 'forward' | 'backward' | 'right' | 'left') => {
            const totalCount = lashings.reduce((sum, l) => sum + l.count, 0);
            if (totalCount === 0) return;

            // Determine model-space direction (includes Breakbulk Transverse rotation)
            let effectiveDir = dir;
            if (transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') {
                if (dir === 'forward') effectiveDir = 'backward';
                else if (dir === 'left') effectiveDir = 'right';
                else if (dir === 'backward') effectiveDir = 'forward';
                else if (dir === 'right') effectiveDir = 'left';
            }

            const ATTACH_INSET = LASHING_ATTACH_INSET;
            // Model faces Forward/Backward (±X) have span along Z axis (depth).
            // Model faces Right/Left (±Z) have span along X axis (width).
            const isModelXFace = (effectiveDir === 'forward' || effectiveDir === 'backward');
            const totalSpan = isModelXFace ? depth : width;
            const usableSpan = Math.max(0, totalSpan - 2 * ATTACH_INSET);
            const step = totalCount > 1 ? usableSpan / (totalCount - 1) : 0;

            let globalIdx = 0;
            lashings.forEach(lashingGroup => {
                const color = lashingGroup.color;
                const positionFlag = lashingGroup.position || 'above';
                const angle = lashingGroup.angle; // Capture manual angle

                let groupStartY = halfH * 0.5;
                if (cog) {
                    if (positionFlag === 'below') {
                        // Below CoG: position as low as possible (near bottom of cargo)
                        groupStartY = -halfH + 0.35;
                    } else {
                        // Above CoG: position as high as possible (near top of cargo)
                        groupStartY = halfH * 0.85;
                    }
                }

                const minStartY = -halfH + 0.15;
                groupStartY = Math.max(groupStartY, minStartY);

                for (let i = 0; i < lashingGroup.count; i++) {
                    // Alternating pattern: odd indices at edges, even indices toward center
                    const slotIdx = globalIdx % 2 === 0
                        ? Math.floor(globalIdx / 2)
                        : totalCount - 1 - Math.floor(globalIdx / 2);

                    // Sign: use effectiveDir to determine direction of the line.
                    // For Model Right/Left faces (span along X), 1 starts from Back (-X) towards Front (+X)?
                    // Wait, legacy code used (dir === 'right' || dir === 'left' ? -1 : 1).
                    // If effectiveDir is Right/Left, use -1.
                    const offset = totalCount > 1
                        ? ((effectiveDir === 'right' || effectiveDir === 'left' ? -1 : 1) * (-usableSpan / 2 + slotIdx * step))
                        : 0;

                    let startPos: [number, number, number];
                    let startRotation: [number, number, number];

                    if (effectiveDir === 'forward') {
                        startPos = [halfL, groupStartY, offset];
                        startRotation = [0, Math.PI / 2, 0];
                    } else if (effectiveDir === 'backward') {
                        startPos = [-halfL, groupStartY, offset];
                        startRotation = [0, -Math.PI / 2, 0];
                    } else if (effectiveDir === 'right') {
                        startPos = [offset, groupStartY, halfW];
                        startRotation = [0, 0, 0];
                    } else { // left
                        startPos = [offset, groupStartY, -halfW];
                        startRotation = [0, Math.PI, 0];
                    }

                    allLashings.push({
                        start: startPos,
                        color,
                        startRotation,
                        direction: effectiveDir, // Track direction for clustering (rotated model-space direction)
                        angle, // Store angle
                    });

                    globalIdx++;
                }
            });
        };

        // Collect all lashings from all directions
        collectLashings(lashingDetails.direct_lashings.forward, 'forward');
        collectLashings(lashingDetails.direct_lashings.backward, 'backward');
        collectLashings(lashingDetails.direct_lashings.right, 'right');
        collectLashings(lashingDetails.direct_lashings.left, 'left');

        // Now distribute lashings to two parallel floor lines, clustered by direction
        const totalLashings = allLashings.length;
        if (totalLashings > 0) {
            const scaleFactor = dimensions.scale || 1;

            // Standard: Right is +Z, Left is -Z
            let maxZ = halfW + 0.8 * scaleFactor;

            const rightLineZ = maxZ; // Right side (+Z)
            const leftLineZ = -maxZ; // Left side (-Z)

            // Group lashings by direction
            const forwardLashings = allLashings.filter(l => l.direction === 'forward');
            const backwardLashings = allLashings.filter(l => l.direction === 'backward');
            const rightLashings = allLashings.filter(l => l.direction === 'right');
            const leftLashings = allLashings.filter(l => l.direction === 'left');

            const ANCHOR_SPACING = 0.4; // Spacing between consecutive anchors

            // Helper to place anchors for a direction
            const placeAnchors = (lashings: typeof allLashings, startX: number, direction: 1 | -1) => {
                for (let i = 0; i < lashings.length; i++) {
                    const lashing = lashings[i];
                    const isRightLine = i % 2 === 0;
                    const lineIndex = Math.floor(i / 2);

                    // 90-degree lashings (vertical/straight) should not have sideways pull (Z-axis for Fwd/Bwd faces)
                    const is90 = lashing.angle === 90;

                    let anchorX = startX + (direction * lineIndex * ANCHOR_SPACING);
                    let anchorZ = is90 ? lashing.start[2] : (isRightLine ? rightLineZ : leftLineZ);
                    let anchorY = -halfH + 0.06;
                    let midPoint: [number, number, number] | undefined = undefined;

                    lashingSegments.push({
                        start: lashing.start,
                        end: [anchorX, anchorY, anchorZ],
                        midPoint,
                        color: lashing.color,
                        startRotation: lashing.startRotation,
                    });
                }
            };

            // Use halfL (which is finalWidth / 2) for anchor placement
            // Forward lashings (+X)
            const forwardPairs = Math.ceil(forwardLashings.length / 2);
            const forwardStartX = halfL + (0.8 * scaleFactor) + (forwardPairs - 1) * (ANCHOR_SPACING * scaleFactor);
            placeAnchors(forwardLashings, forwardStartX, -1); 

            // Backward lashings (-X)
            const backwardPairs = Math.ceil(backwardLashings.length / 2);
            const backwardStartX = -halfL - (0.8 * scaleFactor) - (backwardPairs - 1) * (ANCHOR_SPACING * scaleFactor);
            placeAnchors(backwardLashings, backwardStartX, 1);

            // Helper to place SIDE (Right/Left) lashings with 90-degree logic
            const placeSideLashings = (lashingsInput: typeof allLashings, targetFloorZ: number) => {
                const count = lashingsInput.length;
                if (count === 0) return;

                // --- STEP 0: EXTREMITY RE-MAPPING STRATEGY ---
                // User Goal: "One forward, one backward" for verticals. Distribute 90s to extremities.

                // 1. Extract and Sort Spatial Slots (Geometry)
                // These are the physical attachment points on the cargo.
                const sortedSlots = lashingsInput.map(l => l.start).sort((a, b) => a[0] - b[0]);

                // 2. Separate Definitions
                const verticalDefs = lashingsInput.filter(l => l.angle === 90);
                const standardDefs = lashingsInput.filter(l => l.angle !== 90);

                // 3. Assign Slots (Zig-Zag / Outside-In Strategy)
                // We construct a new array `arrangedLashings` of size `count`.
                const arrangedLashings = new Array(count).fill(null);

                let left = 0;
                let right = count - 1;

                // A. Distribute Verticals to Extremities (Front/Back alternating)
                verticalDefs.forEach((def, i) => {
                    if (i % 2 === 0) {
                        // Even indices -> Left/Back end (or Front depending on sort direction, but strictly "One End")
                        arrangedLashings[left] = def;
                        left++;
                    } else {
                        // Odd indices -> Right/Front end
                        arrangedLashings[right] = def;
                        right--;
                    }
                });

                // B. Distribute Standards to Remaining Slots (Center)
                // We reverse standardDefs so that the LAST added lashing groups (e.g. heavier MSL10000)
                // get placed on the OUTSIDE of the cargo first, which aligns better with stabilization logic.
                standardDefs.reverse();

                // Fill nulls outside-in (zigzag) to ensure symmetric grouping
                const freeSlotIndices: number[] = [];
                for (let i = 0; i < count; i++) {
                    if (arrangedLashings[i] === null) {
                        freeSlotIndices.push(i);
                    }
                }

                let stdIndex = 0;
                let fLeft = 0;
                let fRight = freeSlotIndices.length - 1;

                while (fLeft <= fRight && stdIndex < standardDefs.length) {
                    arrangedLashings[freeSlotIndices[fLeft]] = standardDefs[stdIndex++];
                    if (fLeft !== fRight && stdIndex < standardDefs.length) {
                        arrangedLashings[freeSlotIndices[fRight]] = standardDefs[stdIndex++];
                    }
                    fLeft++;
                    fRight--;
                }

                // 4. Re-Zip: Create new "Virtual Lashings" with assigned slots
                const lashings = arrangedLashings.map((def, i) => {
                    if (!def) return def;
                    return {
                        ...def,
                        // Keeps the mapped horizontal position but retains original height
                        start: [sortedSlots[i][0], def.start[1], sortedSlots[i][2]] as [number, number, number],
                    };
                });

                // --- STEP 1: PREPARE GEOMETRY ---

                // Generate Virtual Anchor Slots (Legacy Geometry)
                const span = halfL * 2;
                const step = count > 1 ? span / (count - 1) : 0;

                // Track reserved slots
                const reservedIndices = new Set<number>();

                // PASS 1: DRAW VERTICALS (Five, 90-degree)
                // User requested strictly drawing these first.
                lashings.forEach((lashing, i) => {
                    if (lashing?.angle === 90) {
                        // 90-degree side lashings are perpendicular to the face (constant X) 
                        // but extend to the parallel virtual anchor line (targetFloorZ) like other ropes.
                        const anchorX = lashing.start[0]; 
                        const anchorZ = targetFloorZ;
                        let anchorY = -halfH + 0.06;

                        let midPoint: [number, number, number] | undefined = undefined;

                        lashingSegments.push({
                            start: lashing.start,
                            end: [anchorX, anchorY, anchorZ],
                            midPoint,
                            color: lashing.color,
                            startRotation: lashing.startRotation,
                        });

                        // Reserve the corresponding slot index
                        reservedIndices.add(i);
                    }
                });

                // Prepare Free Slots for Standards (The Inner Slots)
                const allAnchorXs: number[] = [];
                const freeIndices: number[] = [];
                for (let i = 0; i < count; i++) {
                    const x = -halfL + i * step;
                    allAnchorXs.push(x);

                    if (!reservedIndices.has(i)) {
                        freeIndices.push(i);
                    }
                }

                // PASS 2: DRAW STANDARDS (45-degree) using SIMPLE LEGACY CROSS
                // The remaining inner ropes connect to the remaining inner slots in reverse order (Standard Cross).

                // standard ropes are the non-90 ones remaining in `lashings` (which map to sorted slots)
                // Note: arrangedLashings puts standards in the middle slots, so they are filtered and counted here to be safe
                const standardRopes = lashings.filter(l => l && l.angle !== 90);
                const stdCount = standardRopes.length;

                if (stdCount > 0 && freeIndices.length === stdCount) {
                    const half = stdCount / 2;
                    for (let k = 0; k < stdCount; k++) {
                        const lashing = standardRopes[k];

                        // Split-Shift Logic (User Requested):
                        // For 6 ropes: 1->4, 2->5, 3->6, 4->1, 5->2, 6->3
                        // For 4 ropes: 1->3, 2->4, 3->1, 4->2

                        let targetIndex;
                        if (stdCount % 2 === 0) {
                            // Perfect Split-Shift for even counts
                            targetIndex = k < half ? k + Math.floor(half) : k - Math.floor(half);
                        } else {
                            // Legacy Simple Cross fallback for odd counts
                            targetIndex = stdCount - 1 - k;
                        }

                        if (targetIndex >= 0 && targetIndex < freeIndices.length) {
                            const globalAnchorIndex = freeIndices[targetIndex];

                            const anchorX = allAnchorXs[globalAnchorIndex];
                            // Offset Y so anchors sit visibly on top of the grid
                            let anchorY = -halfH + 0.06;

                            let anchorZ = targetFloorZ;

                            let midPoint: [number, number, number] | undefined = undefined;

                            lashingSegments.push({
                                start: lashing.start,
                                end: [anchorX, anchorY, anchorZ],
                                midPoint,
                                color: lashing.color,
                                startRotation: lashing.startRotation,
                            });
                        }
                    }
                }
            };

            // Right lashings: Cargo (+Z) -> Floor (+Z Line)
            placeSideLashings(rightLashings, rightLineZ);
            // Left lashings: Cargo (-Z) -> Floor (-Z Line)
            placeSideLashings(leftLashings, leftLineZ);
        }
    }

    return (
        <group>
            {/* Direction Labels with Breakbulk Transverse adjustment */}
            {(() => {
                let fwdLabel = "Forward";
                let bwdLabel = "Backward";
                let rightLabel = "Right";
                let leftLabel = "Left";

                if (transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') {
                    // Match 2D Diagram Layout and Physical Securing Logic:
                    // SHIP Forward (+X) face is at Model +X. Label: "Backward" (Against Backward)
                    // SHIP Aft (-X) face is at Model -X. Label: "Forward" (Against Forward)
                    // SHIP Starboard (+Z) face is at Model +Z. Label: "Left" (Against Left)
                    // SHIP Port (-Z) face is at Model -Z. Label: "Right" (Against Right)
                    fwdLabel = "Backward";
                    bwdLabel = "Forward";
                    rightLabel = "Left";
                    leftLabel = "Right";
                }

                return (
                    <>
                        <AxisLabel position={[labelOffsetL, 0.2, 0]} text={fwdLabel} color={axisColorZ} scale={responsiveLabelScale} />
                        <AxisLabel position={[-labelOffsetL, 0.2, 0]} text={bwdLabel} color={axisColorZ} scale={responsiveLabelScale} />
                        <AxisLabel position={[0, 0.2, labelOffsetW]} text={rightLabel} color={axisColorZ} scale={responsiveLabelScale} />
                        <AxisLabel position={[0, 0.2, -labelOffsetW]} text={leftLabel} color={axisColorZ} scale={responsiveLabelScale} />
                    </>
                );
            })()}

            <CargoBox dimensions={{ width, height, depth }} theme={theme} opacity={1} />


            {lashingDetails && (
                <>
                    {(() => {
                        // Effective mapping of data to model positions
                        let fwd = lashingDetails.direct_lashings.forward;
                        let bwd = lashingDetails.direct_lashings.backward;
                        let right = lashingDetails.direct_lashings.right;
                        let left = lashingDetails.direct_lashings.left;

                        if (transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') {
                            // Match drawn segments from collectLashings mapping:
                            // Data Forward  -> Model Backward (-X) -> Labeled "Forward" (bwd var)
                            // Data Backward -> Model Forward (+X)  -> Labeled "Backward" (fwd var)
                            // Data Right    -> Model Left (-Z)     -> Labeled "Right" (left var)
                            // Data Left     -> Model Right (+Z)    -> Labeled "Left" (right var)
                            fwd = lashingDetails.direct_lashings.backward;
                            bwd = lashingDetails.direct_lashings.forward;
                            right = lashingDetails.direct_lashings.left;
                            left = lashingDetails.direct_lashings.right;
                        }

                        return (
                            <>
                                {fwd.length > 0 && (
                                    <AxisLabel
                                        position={[halfL + 2.4, -halfH + 0.4, 0]}
                                        segments={fwd.map(l => ({ text: `${l.count} x ${l.msl_tur || l.type || 'Direct'}`, color: l.color }))}
                                        scale={responsiveLabelScale * 0.8}
                                    />
                                )}
                                {bwd.length > 0 && (
                                    <AxisLabel
                                        position={[-halfL - 2.4, -halfH + 0.4, 0]}
                                        segments={bwd.map(l => ({ text: `${l.count} x ${l.msl_tur || l.type || 'Direct'}`, color: l.color }))}
                                        scale={responsiveLabelScale * 0.8}
                                    />
                                )}
                                {right.length > 0 && (
                                    <AxisLabel
                                        position={[0, -halfH + 0.4, halfW + 2.4]}
                                        segments={right.map(l => ({ text: `${l.count} x ${l.msl_tur || l.type || 'Direct'}`, color: l.color }))}
                                        scale={responsiveLabelScale * 0.8}
                                    />
                                )}
                                {left.length > 0 && (
                                    <AxisLabel
                                        position={[0, -halfH + 0.4, -halfW - 2.4]}
                                        segments={left.map(l => ({ text: `${l.count} x ${l.msl_tur || l.type || 'Direct'}`, color: l.color }))}
                                        scale={responsiveLabelScale * 0.8}
                                    />
                                )}
                            </>
                        );
                    })()}

                    {lashingSegments.map((segment, idx) => (
                        <React.Fragment key={`lashing-group-${idx}`}>
                            <DirectLashing
                                startPoint={segment.start}
                                endPoint={segment.end}
                                midPoint={segment.midPoint}
                                color={segment.color}
                                startRotation={segment.startRotation}
                            />
                            <DRing position={segment.end} scale={0.4} color={isDark ? '#64748b' : '#94a3b8'} />
                        </React.Fragment>
                    ))}

                    {lashingDetails.loop_lashing.enabled && lashingDetails.loop_lashing.count > 0 && (
                        <>
                                <AxisLabel
                                position={[0, halfH + 0.4, 0]}
                                text={`${lashingDetails.loop_lashing.count} x ${lashingDetails.loop_lashing.msl_tur || lashingDetails.loop_lashing.type}`}
                                color={lashingDetails.loop_lashing.color}
                                scale={responsiveLabelScale * 0.8}
                            />
                            {Array.from({ length: lashingDetails.loop_lashing.count }).map((_, idx) => {
                                const count = lashingDetails.loop_lashing.count;
                                const isTransverse = transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse';
                                
                                // In Transverse mode:
                                // - distribution is along Z (depth) instead of X (width)
                                // - width of cargo to wrap across is 'width' (X)
                                // - depth of cargo to distribute along is 'depth' (Z)
                                
                                let distPos = 0;
                                const distDim = isTransverse ? depth : width;
                                const halfDistDim = distDim / 2;
                                
                                if (count === 1) {
                                    distPos = 0;
                                } else {
                                    const numPairs = Math.ceil(count / 2);
                                    const pairIdx = Math.floor(idx / 2);
                                    const inPairIdx = idx % 2;
                                    const margin = distDim * 0.15;
                                    const availableRange = distDim - 2 * margin;
                                    const pairSpacing = numPairs > 1 ? availableRange / (numPairs - 1) : 0;
                                    const pairCenter = -halfDistDim + margin + (pairIdx * pairSpacing);
                                    const coupleGap = 0.15;
                                    const isLastOdd = (count % 2 !== 0 && pairIdx === numPairs - 1);
                                    if (isLastOdd) {
                                        distPos = pairCenter;
                                    } else {
                                        distPos = pairCenter + (inPairIdx - 0.5) * coupleGap;
                                    }
                                }
                                
                                const anchorSide = idx % 2 === 0 ? 'right' : 'left';
                                const scaleFactor = dimensions.scale || 1;
                                let anchorOffset = 0.8 * scaleFactor;
                                let anchorY = -halfH + 0.06;

                                if (isTransverse) {
                                    // TRANSVERSE: wrap across X-axis, distribute along Z-axis
                                    const anchorX = anchorSide === 'right' ? halfL + anchorOffset : -halfL - anchorOffset;
                                    return (
                                        <React.Fragment key={`loop-lashing-group-${idx}`}>
                                            <LoopLashing
                                                position={[0, -halfH, distPos]} // distributed along Z
                                                depth={width} // wraps across X
                                                height={height}
                                                color={lashingDetails.loop_lashing.color}
                                                anchorSide={anchorSide === 'right' ? 'forward' : 'backward'}
                                                anchorX={anchorX}
                                                anchorYOffset={anchorY - (-halfH)}
                                                axis="x"
                                            />
                                            <DRing position={[anchorX, anchorY, distPos]} scale={0.4} color={isDark ? '#64748b' : '#94a3b8'} />
                                        </React.Fragment>
                                    );
                                } else {
                                    // LONGITUDINAL: wrap across Z-axis, distribute along X-axis
                                    const anchorZ = anchorSide === 'right' ? halfW + anchorOffset : -halfW - anchorOffset;
                                    return (
                                        <React.Fragment key={`loop-lashing-group-${idx}`}>
                                            <LoopLashing
                                                position={[distPos, -halfH, 0]} // distributed along X
                                                depth={depth} // wraps across Z
                                                height={height}
                                                color={lashingDetails.loop_lashing.color}
                                                anchorSide={anchorSide}
                                                anchorZ={anchorZ}
                                                anchorYOffset={anchorY - (-halfH)}
                                                axis="z"
                                            />
                                            <DRing position={[distPos, anchorY, anchorZ]} scale={0.4} color={isDark ? '#64748b' : '#94a3b8'} />
                                        </React.Fragment>
                                    );
                                }
                            })}
                        </>
                    )}

                    {(() => {
                        const scale = dimensions.scale || 1;

                        const getRealDims = () => {
                            // Developer Note: User requested fixed 10x10cm visual sizes for the blocks
                            // Protrusion/Thickness (p) -> How far it sticks out from the cargo
                            const p_base = 0.7 * scale; // Set to 0.7 as per user request
                            // Vertical Height (h) -> Fixed at 10cm (0.1m)
                            const h_real = 0.1 * scale;
                            // Width along face (w) -> Fixed at 10cm (0.1m)
                            const w_real = 0.1 * scale;
                            return { h: h_real, w: w_real, p: p_base };
                        };

                        return (
                            <>
                                {(() => {
                                    // Effective mapping of supports to model positions
                                    let supports = lashingDetails.supports;

                                    if (transportUnit === 'breakbulk' && breakBulkOrientation === 'transverse') {
                                        // Match coordinate mapping used for labels and lashings:
                                        // - Data Forward  -> Model Backward
                                        // - Data Backward -> Model Forward
                                        // - Data Right    -> Model Left
                                        // - Data Left     -> Model Right
                                        supports = {
                                            forward: lashingDetails.supports.backward,
                                            backward: lashingDetails.supports.forward,
                                            right: lashingDetails.supports.left,
                                            left: lashingDetails.supports.right
                                        };
                                    }

                                    const { h, w, p } = getRealDims();
                                    const floorY = -halfH + h / 2;

                                    return (
                                        <>
                                            {supports.forward.count > 0 && supports.forward.type !== 'none' && (
                                                Array.from({ length: supports.forward.count }).map((_, idx) => {
                                                    const spacing = depth / (supports.forward.count + 1);
                                                    const zPos = -halfW + spacing * (idx + 1);
                                                    const isStopper = supports.forward.type === 'stopper';
                                                    let currentProtrusion = isStopper ? p * 0.5 : p;
                                                    return <SupportBlock key={`support-forward-${idx}`} position={[halfL + currentProtrusion / 2, floorY, zPos]} size={[currentProtrusion, h, w]} type={isStopper ? 'stopper' : 'wedge'} side="forward" />;
                                                })
                                            )}
                                            {supports.backward.count > 0 && supports.backward.type !== 'none' && (
                                                Array.from({ length: supports.backward.count }).map((_, idx) => {
                                                    const spacing = depth / (supports.backward.count + 1);
                                                    const zPos = -halfW + spacing * (idx + 1);
                                                    const isStopper = supports.backward.type === 'stopper';
                                                    let currentProtrusion = isStopper ? p * 0.5 : p;
                                                    return <SupportBlock key={`support-backward-${idx}`} position={[-halfL - currentProtrusion / 2, floorY, zPos]} size={[currentProtrusion, h, w]} type={isStopper ? 'stopper' : 'wedge'} side="backward" />;
                                                })
                                            )}
                                            {supports.right.count > 0 && supports.right.type !== 'none' && (
                                                Array.from({ length: supports.right.count }).map((_, idx) => {
                                                    const spacing = width / (supports.right.count + 1);
                                                    const xPos = -halfL + spacing * (idx + 1);
                                                    const isStopper = supports.right.type === 'stopper';
                                                    let currentProtrusion = isStopper ? p * 0.5 : p;
                                                    return <SupportBlock key={`support-right-${idx}`} position={[xPos, floorY, halfW + currentProtrusion / 2]} size={[currentProtrusion, h, w]} type={isStopper ? 'stopper' : 'wedge'} side="right" />;
                                                })
                                            )}
                                            {supports.left.count > 0 && supports.left.type !== 'none' && (
                                                Array.from({ length: supports.left.count }).map((_, idx) => {
                                                    const spacing = width / (supports.left.count + 1);
                                                    const xPos = -halfL + spacing * (idx + 1);
                                                    const isStopper = supports.left.type === 'stopper';
                                                    let currentProtrusion = isStopper ? p * 0.5 : p;
                                                    return <SupportBlock key={`support-left-${idx}`} position={[xPos, floorY, -halfW - currentProtrusion / 2]} size={[currentProtrusion, h, w]} type={isStopper ? 'stopper' : 'wedge'} side="left" />;
                                                })
                                            )}
                                        </>
                                    );
                                })()}
                            </>
                        );
                    })()}
                </>
            )}
        </group>
    );
});

LashingPrism.displayName = 'LashingPrism';

const LashingScene: React.FC<LashingSceneProps> = React.memo(({ dimensions, cog, theme = 'dark', lashingDetails, zoom, onZoomChange, viewPreset, cameraAngle, transportUnit, breakBulkOrientation, targetYOffset, isFullscreen }) => {
    const isDark = theme === 'dark';
    const handleCreated = ({ gl }: any) => {
        // Trap the WebGL Context Loss to prevent silent GPU failures during tab swaps or HMR
        gl.domElement.addEventListener('webglcontextlost', (e: Event) => {
            e.preventDefault();
            console.warn('THREE.WebGLRenderer: Context Lost. React Three Fiber will attempt to recover or the page may need a refresh depending on GPU pressure.');
        });
    };

    return (
        <Canvas 
            onCreated={handleCreated}
            shadows 
            camera={{ position: DEFAULT_CAMERA_POSITION, fov: DEFAULT_CAMERA_FOV }} 
            gl={{ alpha: false, preserveDrawingBuffer: true, powerPreference: 'high-performance' }}
        >
            <color attach="background" args={[isDark ? '#0D111C' : '#f8fafc']} />
            <ambientLight intensity={isDark ? AMBIENT_LIGHT_INTENSITY_DARK : AMBIENT_LIGHT_INTENSITY_LIGHT} />
            <directionalLight
                position={[5, 10, 5]}
                intensity={isDark ? DIRECTIONAL_LIGHT_INTENSITY_DARK : DIRECTIONAL_LIGHT_INTENSITY_LIGHT}
                color={isDark ? '#e5e7eb' : '#ffffff'}
            />
            <CameraController 
                zoom={zoom} 
                onZoomChange={onZoomChange} 
                viewPreset={viewPreset} 
                cameraAngle={cameraAngle} 
                targetYOffset={targetYOffset} 
                dimensions={dimensions} 
            />
            <LashingPrism dimensions={dimensions} cog={cog} theme={theme} lashingDetails={lashingDetails} viewPreset={viewPreset} transportUnit={transportUnit} breakBulkOrientation={breakBulkOrientation} isFullscreen={isFullscreen} />

            {/* Simple Grid Base aligned with the bottom of the cargo */}
            <gridHelper
                args={[
                    100, // Large size to cover screen
                    100, // High density of lines
                    isDark ? '#4b5563' : '#cbd5e1', // Center line color
                    isDark ? '#334155' : '#e2e8f0', // Grid line color
                ]}
                position={[0, -dimensions.height / 2, 0]} // Align with the bottom of the cargo
            />
        </Canvas>
    );
});

LashingScene.displayName = 'LashingScene';
export default LashingScene;
